import React from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { Switch } from '@mui/material';
import FieldRow from 'components/FormElements/FieldRow';
import TextField from 'components/FormElements/AppTextInput';
import CkEditor from 'components/CkEditor';
import AppCheckbox from 'components/FormElements/AppCheckBox';
import { AppSelectBox } from 'components/FormElements';
import { DeliveryType } from 'further-types/custom-updates';
import useFileUpload from 'hooks/ui/useFileUpload';
import { UploadType } from 'further-types/files';
import { useNotification } from 'hooks/ui/useNotification';
import Dropzone from 'components/Dropzone';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useFunds } from 'hooks/data/fund/useFunds';
import RecordView from 'components/RecordView';
import Divider from '@mui/material/Divider';
import MultiInvestorSelector from 'components/MultiInvestorSelector';
import AllocationSelector from 'components/AllocationSelector';
import { Api } from 'further-types/custom-updates';
import MultiCustomTagSelector from 'components/MultiCustomTagSelector';
import MultiFirmCompanySelector from 'components/CompanyMultiSelect';
import TranchesMultiSelect from 'components/TrancheMultiSelect';
import { UploadedFile } from 'components/Dropzone';
import { CustomUpdateStyleClasses } from './index';
import { FirmSelector } from 'components/FirmSelector';
import { useSortTranches } from 'hooks/ui/useSortTranches';

interface CreateCustomUpdateRequest
  extends Omit<Api.CreateCustomUpdateRequest, 'recipientFilter'> {
  recipients: Array<Api.Recipient>;
  recipientFilter: Api.RecipientFilter | '';
}

type Props = {
  classes: CustomUpdateStyleClasses;
  formData: CreateCustomUpdateRequest | Api.UpdateCustomUpdateRequest;
  errorText: Record<string, string>;
  setValue: UseFormSetValue<CreateCustomUpdateRequest>;
  firmId: string | undefined;
};

const isUpdate = (
  formData: CreateCustomUpdateRequest | Api.UpdateCustomUpdateRequest,
): formData is Api.UpdateCustomUpdateRequest => {
  return '_id' in formData;
};

const AddCustomUpdateDetails: React.FC<Props> = ({
  classes,
  formData,
  errorText,
  setValue,
  firmId,
}) => {
  const { uploadFile } = useFileUpload();
  const { error } = useNotification();
  const { isSuperAdmin } = useGetRole();

  const { tranches } = useFunds({
    fundStatus: [0, 1],
    leanResponse: true,
    firmId: formData.firmId || undefined,
  });
  const sortedTranches = useSortTranches(tranches?.data);

  const getRecipientFilterLabel = (filter: Api.RecipientFilter): string => {
    switch (filter) {
      case Api.RecipientFilter.Specific:
        return 'By specific investors';
      case Api.RecipientFilter.All:
        return 'To all registered users';
      case Api.RecipientFilter.Subscriptions:
        return 'To all investors with subscriptions';
      case Api.RecipientFilter.NoSubscriptions:
        return 'To all investors without subscriptions';
      case Api.RecipientFilter.SubscriptionCustomTags:
        return 'By subscription tag';
      case Api.RecipientFilter.InvestorCustomTags:
        return 'By investor tag';
      default:
        return filter;
    }
  };

  async function uploadMedia(file: File) {
    const { data, error: uploadError } = await uploadFile(
      file,
      UploadType.CustomUpdateAttachments,
    );
    if (data) {
      return data.filePath;
    } else {
      error(uploadError.message);
    }
  }

  const handleChangeAttachment = async (files: Array<UploadedFile>) => {
    const filesToUpload: Array<Api.CustomUpdateAttachment | undefined> =
      await Promise.all(
        files.map(
          async (file): Promise<Api.CustomUpdateAttachment | undefined> => {
            if (file.path) {
              const filePath = await uploadMedia(file);
              if (!filePath) return undefined;
              return {
                title: file?.name,
                url: filePath,
                preview: file?.preview,
              };
            }
            return undefined;
          },
        ),
      );
    const currentAttachments = formData.attachments || [];
    const validFiles = filesToUpload.filter(
      (file): file is Api.CustomUpdateAttachment => file !== undefined,
    );

    setValue('attachments', [...(currentAttachments || []), ...validFiles]);
  };

  const handleRemoveAttachment = (fileIdx: number) => {
    const currentAttachments = formData.attachments || [];
    const newAttachments = currentAttachments.filter(
      (_attachment, idx) => idx !== fileIdx,
    );
    // Force a new array reference to ensure React re-renders
    setValue('attachments', [...newAttachments], {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleUpdateSendToAllUsers = (
    sendToAllUsers: boolean,
    sendToAllUsersWithSubscriptions: boolean,
    sendToAllUsersWithNoSubscriptions: boolean,
    recipientFilter: Api.RecipientFilter,
  ) => {
    setValue('sendToAllUsers', sendToAllUsers);
    setValue(
      'sendToAllUsersWithSubscriptions',
      sendToAllUsersWithSubscriptions,
    );
    setValue(
      'sendToAllUsersWithNoSubscriptions',
      sendToAllUsersWithNoSubscriptions,
    );
    setValue('recipientFilter', recipientFilter);

    if (recipientFilter === Api.RecipientFilter.InvestorCustomTags) {
      setValue('sendToAdvisers', false);
      setValue('sendToOrganisations', false);
      setValue('sendToAllUsers', true);
    }

    const trancheIds =
      sendToAllUsers ||
      sendToAllUsersWithSubscriptions ||
      sendToAllUsersWithNoSubscriptions ||
      recipientFilter === Api.RecipientFilter.InvestorCustomTags
        ? sortedTranches.map((tranche) => tranche._id)
        : [];

    setValue('trancheId', trancheIds);
    setValue('companyId', []);
    setValue('allocationIds', []);
    setValue('investorIds', []);
    setValue('customTags', []);
  };

  return (
    <RecordView>
      {isSuperAdmin && (
        <FieldRow title="Select firm">
          <FirmSelector
            firmId={formData.firmId}
            onChange={(value) => {
              setValue('firmId', value);
              // Clear tranche and company selections when firm changes
              setValue('trancheId', []);
              setValue('companyId', []);
              setValue('allocationIds', []);
            }}
            disabled={!!isUpdate(formData)}
            error={!!errorText?.firmId}
            helperText={errorText?.firmId}
          />
        </FieldRow>
      )}
      <FieldRow title="Select recipient filter">
        <AppSelectBox
          labelKey="label"
          valueKey="value"
          placeholder="Select a filter"
          displayEmpty
          disabled={!formData.firmId}
          data={[
            {
              label: 'By company and allocation',
              value: Api.RecipientFilter.Company,
            },
            { label: 'By tranche', value: Api.RecipientFilter.Tranche },
            ...(isSuperAdmin
              ? []
              : [
                  {
                    label: 'By specific investors',
                    value: Api.RecipientFilter.Specific,
                  },
                  {
                    label: 'To all registered users',
                    value: Api.RecipientFilter.All,
                  },
                  {
                    label: 'To all investors with subscriptions',
                    value: Api.RecipientFilter.Subscriptions,
                  },
                  {
                    label: 'To all investors without subscriptions',
                    value: Api.RecipientFilter.NoSubscriptions,
                  },
                  {
                    label: 'By subscription tag',
                    value: Api.RecipientFilter.SubscriptionCustomTags,
                  },
                  {
                    label: 'By investor tag',
                    value: Api.RecipientFilter.InvestorCustomTags,
                  },
                ]),
            // Add the current value if it's not in the available options (for viewing existing custom updates)
            ...(isSuperAdmin &&
            formData.recipientFilter &&
            ![
              Api.RecipientFilter.Company,
              Api.RecipientFilter.Tranche,
            ].includes(formData.recipientFilter)
              ? [
                  {
                    label: getRecipientFilterLabel(formData.recipientFilter),
                    value: formData.recipientFilter,
                    disabled: true,
                  },
                ]
              : []),
          ]}
          error={!!errorText?.recipientFilter}
          helperText={errorText?.recipientFilter}
          value={formData.recipientFilter}
          onChange={(event) => {
            handleUpdateSendToAllUsers(
              event.target.value === Api.RecipientFilter.All,
              event.target.value === Api.RecipientFilter.Subscriptions,
              event.target.value === Api.RecipientFilter.NoSubscriptions,
              event.target.value as Api.RecipientFilter,
            );
          }}
        />
      </FieldRow>

      {formData.recipientFilter === Api.RecipientFilter.Company && (
        <>
          <FieldRow title="Select company">
            <MultiFirmCompanySelector
              handleCompanyMultiSelectChanges={(value) => {
                setValue('companyId', value.selectedCompanyIds);
                setValue('allocationIds', []);
                setValue('investorIds', []);
              }}
              error={errorText?.companyId}
              disabled={
                !!(formData?.trancheId?.length || formData?.sendToAllUsers)
              }
              value={formData?.companyId}
              key={`company-selector-${
                formData.firmId
              }-${!!formData?.sendToAllUsers}`}
              firmId={formData.firmId}
            />
          </FieldRow>

          <FieldRow title="Select allocation">
            <AllocationSelector
              key={`allocation-selector-${
                formData.firmId
              }-${formData?.companyId?.join(',')}`}
              firmId={formData.firmId || firmId}
              companyIds={formData?.companyId}
              value={formData?.allocationIds}
              onChange={(value) => {
                setValue('allocationIds', value);
              }}
              disabled={!formData?.companyId?.length}
              selectAllAllocationsByDefault
            />
          </FieldRow>
        </>
      )}

      {formData.recipientFilter === Api.RecipientFilter.Tranche && (
        <FieldRow title="Select tranche(s)">
          <TranchesMultiSelect
            key={`tranche-selector-${formData.firmId}`}
            tranches={sortedTranches}
            handleChangeSelectedTranches={(trancheIds) => {
              setValue('trancheId', trancheIds);
              setValue('investorIds', []);
            }}
            disabled={
              !!(formData?.companyId?.length || formData?.sendToAllUsers)
            }
            value={formData?.trancheId}
          />
        </FieldRow>
      )}

      {formData.recipientFilter === Api.RecipientFilter.Specific && (
        <FieldRow title="Select specific investors">
          <MultiInvestorSelector
            onChange={(value) => {
              setValue('investorIds', value);
            }}
            error={!!errorText?.investorIds}
            value={formData.investorIds}
            helperText={errorText?.investorIds}
            excludeDisabledEmail={
              formData.deliveryToInvestors === DeliveryType.Email
            }
          />
        </FieldRow>
      )}

      {(formData.recipientFilter ===
        Api.RecipientFilter.SubscriptionCustomTags ||
        formData.recipientFilter ===
          Api.RecipientFilter.InvestorCustomTags) && (
        <FieldRow title="Select custom tags">
          <MultiCustomTagSelector
            mode={
              formData.recipientFilter ===
              Api.RecipientFilter.SubscriptionCustomTags
                ? 'subscription'
                : 'investor'
            }
            firmId={formData.firmId}
            onChange={(value) => {
              setValue('customTags', value);
            }}
            value={formData.customTags}
            error={!!errorText?.customTags}
            helperText={errorText?.customTags}
          />
        </FieldRow>
      )}

      <Divider />

      {formData.recipientFilter !== Api.RecipientFilter.Specific &&
        formData.recipientFilter !== Api.RecipientFilter.Subscriptions &&
        formData.recipientFilter !== Api.RecipientFilter.NoSubscriptions && (
          <FieldRow title="Send to" centerTitle>
            <AppCheckbox
              label="Direct investors"
              key="includeDirectInvestors"
              checked={!!formData.sendToDirectInvestors}
              onChange={(event) => {
                setValue('sendToDirectInvestors', event.target.checked);
              }}
            />
            <AppCheckbox
              label="Advised investors"
              key="includeAdvisedInvestors"
              checked={!!formData.sendToAdvisedInvestors}
              onChange={(event) => {
                setValue('sendToAdvisedInvestors', event.target.checked);
              }}
            />
            {formData.recipientFilter !==
              Api.RecipientFilter.InvestorCustomTags && (
              <>
                <AppCheckbox
                  label="Advisers"
                  key="includeAdvisers"
                  checked={!!formData.sendToAdvisers}
                  onChange={(event) => {
                    setValue('sendToAdvisers', event.target.checked);
                  }}
                />
                <AppCheckbox
                  label="Organisations"
                  key="includeOrganisations"
                  checked={!!formData.sendToOrganisations}
                  onChange={(event) => {
                    setValue('sendToOrganisations', event.target.checked);
                  }}
                />
              </>
            )}

            {errorText?.sendTo && (
              <p className={classes.errorText}>{errorText?.sendTo}</p>
            )}
          </FieldRow>
        )}

      <FieldRow title="Delivery method" centerTitle>
        <AppSelectBox
          data={[
            { label: 'Email only', value: DeliveryType.Email },
            { label: 'Display in portal only', value: DeliveryType.Portal },
            {
              label: 'Email and display in portal',
              value: DeliveryType.EmailAndPortal,
            },
          ]}
          valueKey="value"
          labelKey="label"
          disabled={
            !(
              formData.sendToDirectInvestors ||
              formData.sendToAdvisedInvestors ||
              formData.sendToAdvisers ||
              formData.sendToOrganisations
            ) &&
            formData.recipientFilter !== Api.RecipientFilter.All &&
            formData.recipientFilter !== Api.RecipientFilter.Subscriptions &&
            formData.recipientFilter !== Api.RecipientFilter.NoSubscriptions &&
            formData.recipientFilter !== Api.RecipientFilter.Specific
          }
          value={formData.deliveryToInvestors}
          onChange={(event) => {
            setValue('deliveryToInvestors', event.target.value as DeliveryType);
          }}
        />
      </FieldRow>

      <FieldRow title="Subject line*" centerTitle>
        <TextField
          name="subject"
          placeholder="e.g. An update on Company X's performance"
          required
          fullWidth
          error={!!errorText?.subject}
          helperText={errorText?.subject}
          value={formData.subject}
          onChange={(event) => {
            setValue('subject', event.target.value);
          }}
        />
      </FieldRow>
      <FieldRow title="Body title*" centerTitle>
        <TextField
          name="title"
          placeholder="e.g. Company X - an update"
          required
          fullWidth
          value={formData.title}
          error={!!errorText?.title}
          helperText={errorText?.title}
          onChange={(event) => {
            setValue('title', event.target.value);
          }}
        />
      </FieldRow>
      <FieldRow title="Body text (including sign-off)*" introLineTopTitle>
        Dear $recipientname,
        <CkEditor
          placeholder="e.g. An update on Company X’s performance"
          onChange={(value) => {
            setValue('emailBody', value);
          }}
          onBlur={(value) => {
            setValue('emailBody', value);
          }}
          value={formData.emailBody}
        />
        {errorText?.emailBody && (
          <p className={classes.errorText}>{errorText.emailBody}</p>
        )}
      </FieldRow>

      <FieldRow title="Upload attachment" topTitle>
        <Dropzone
          key={`attachments-${formData.attachments?.length || 0}`}
          name="attachments"
          files={
            formData.attachments.map((file) => ({
              fileName: file.title,
              preview: file.preview || file.url,
            })) ?? []
          }
          onDropFile={handleChangeAttachment}
          onRemove={handleRemoveAttachment}
          maxFiles={10}
          defaultAcceptGroup="filesAndImages"
        />
      </FieldRow>

      <FieldRow
        title="Include UK financial promotions notice at top of email"
        tooltipText="Turning this on will add UK FCA COBS financial promotion wording in a box at the top of your email."
        centerTitle
      >
        <Switch
          checked={formData.includeFinancialPromotionsNotice}
          onChange={(event) => {
            setValue('includeFinancialPromotionsNotice', event.target.checked);
          }}
        />
      </FieldRow>

      {formData.includeFinancialPromotionsNotice && (
        <FieldRow
          title="Learn more URL"
          tooltipText="Enter the URL the 'Take 2 mins to learn more' wording in the FCA warning will direct to."
          centerTitle
        >
          <TextField
            name="financialPromotionsLearnMoreUrl"
            placeholder="Enter full URL"
            required
            fullWidth
            error={!!errorText?.financialPromotionsLearnMoreUrl}
            helperText={errorText?.financialPromotionsLearnMoreUrl}
            value={formData.financialPromotionsLearnMoreUrl}
            onChange={(event) => {
              setValue('financialPromotionsLearnMoreUrl', event.target.value);
            }}
          />
        </FieldRow>
      )}
    </RecordView>
  );
};

export default AddCustomUpdateDetails;
